import mixpanel from 'mixpanel-browser'

/**
 * Mixpanel Event Name Constants
 * Centralized event names for consistent tracking across the application.
 * Using constants prevents typos and makes refactoring easier.
 */

// Authentication Events
export const AUTH_EVENTS = {
	// Sign Up
	SIGN_UP_STARTED: 'Sign Up Started',
	SIGN_UP_COMPLETED: 'Sign Up Completed',
	SIGN_UP_FAILED: 'Sign Up Failed',

	// Sign In
	SIGN_IN_ATTEMPTED: 'Sign In Attempted',
	SIGN_IN_SUCCESS: 'Sign In Success',
	SIGN_IN_FAILED: 'Sign In Failed',

	// Logout
	LOGOUT: 'Logout',
} as const

// User Profile Events
export const PROFILE_EVENTS = {
	AVATAR_UPLOADED: 'Avatar Uploaded',
	PROFILE_UPDATED: 'Profile Updated',
	SOCIAL_CONNECTED: 'Social Account Connected',
	SOCIAL_DISCONNECTED: 'Social Account Disconnected',
	PAYOUT_CONNECTED: 'Payout Account Connected',
	PASSKEY_REGISTERED: 'Passkey Registered',
} as const

// Event-Related Events
export const EVENT_EVENTS = {
	// Viewing
	EVENT_VIEWED: 'Event Viewed',

	// Creation
	EVENT_CREATE_STARTED: 'Event Create Started',
	EVENT_CREATE_STEP_COMPLETED: 'Event Create Step Completed',
	EVENT_CREATE_COMPLETED: 'Event Create Completed',
	EVENT_CREATE_CANCELLED: 'Event Create Cancelled',

	// Editing
	EVENT_EDIT_STARTED: 'Event Edit Started',
	EVENT_EDIT_COMPLETED: 'Event Edit Completed',
	EVENT_EDIT_CANCELLED: 'Event Edit Cancelled',

	// Share
	EVENT_LINK_COPIED: 'Event Link Copied',
	EVENT_PRIVATE_TICKET_LINK_COPIED: 'Event Private Ticket Link Copied',
} as const

// Payment Events
export const PAYMENT_EVENTS = {
	PAYMENT_STARTED: 'Payment Started',
	PAYMENT_COMPLETED: 'Payment Completed',
	PAYMENT_FAILED: 'Payment Failed',
	PAYMENT_METHOD_ADDED: 'Payment Method Added',
	PAYMENT_METHOD_REMOVED: 'Payment Method Removed',
} as const

// Invite Link (Redeem Offer) Events
export const INVITE_LINK_EVENTS = {
	INVITE_LINK_OPENED: 'Invite Link Opened',
	CONTINUE_CLICKED: 'Continue Clicked',
	CANCEL_CLICKED: 'Cancel Clicked',
} as const

// Offer Events
export const OFFER_EVENTS = {
	OFFER_VIEWED: 'Offer Viewed',
	OFFER_CREATE_COMPLETED: 'Offer Create Completed',
	OFFER_EDIT_COMPLETED: 'Offer Edit Completed',
	OFFER_REDEEMED: 'Offer Redeemed',
	OFFER_CUSTOMER_INFO_SUBMITTED: 'Offer Customer Info Submitted',
	OFFER_PURCHASED: 'Offer Purchased',
} as const

// Contact Invite Events
export const CONTACT_INVITE_EVENTS = {
	EVENT_INVITE_SENT: 'Event Invite Sent',
	OFFER_INVITE_SENT: 'Offer Invite Sent',
} as const

// Team Management Events
export const TEAM_EVENTS = {
	TEAM_MEMBER_INVITED: 'Team Member Invited',
	TEAM_COLLABORATOR_INVITED: 'Team Collaborator Invited',
	TEAM_MEMBER_ROLE_UPDATED: 'Team Member Role Updated',
	TEAM_COLLABORATOR_ROLE_UPDATED: 'Team Collaborator Role Updated',
	TEAM_MEMBER_REMOVED: 'Team Member Removed',
	TEAM_COLLABORATOR_REMOVED: 'Team Collaborator Removed',
	CLUB_OWNERSHIP_TRANSFERRED: 'Club Ownership Transferred',
} as const

// Club Management Events
export const CLUB_EVENTS = {
	CLUB_CREATED: 'Club Created',
	CLUB_UPDATED: 'Club Updated',
	CLUB_JOINED: 'Club Joined',
} as const

// RSVP & Ticket Purchase Events
export const RSVP_EVENTS = {
	RSVP_FREE_TICKET: 'RSVP Free Ticket',
	TICKET_PURCHASED: 'Ticket Purchased',
	WAITLIST_JOINED: 'Waitlist Joined',
} as const

// Contact Management Events
export const CONTACT_EVENTS = {
	CONTACTS_IMPORTED: 'Contacts Imported',
	MESSAGE_SENT: 'Message Sent to Contacts',
} as const

// Search Events
export const SEARCH_EVENTS = {
	EVENT_SEARCHED: 'Event Searched',
	EVENT_FILTERED: 'Event Filtered',
} as const

// Export all events as a single object for easy access
export const MIXPANEL_EVENTS = {
	...AUTH_EVENTS,
	...PROFILE_EVENTS,
	...EVENT_EVENTS,
	...PAYMENT_EVENTS,
	...INVITE_LINK_EVENTS,
	...OFFER_EVENTS,
	...CONTACT_INVITE_EVENTS,
	...TEAM_EVENTS,
	...CLUB_EVENTS,
	...RSVP_EVENTS,
	...CONTACT_EVENTS,
	...SEARCH_EVENTS,
} as const

// Type helper for event names
export type MixpanelEventName = (typeof MIXPANEL_EVENTS)[keyof typeof MIXPANEL_EVENTS]

// Initialize Mixpanel
export const initMixpanel = () => {
	const projectToken = import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN

	if (!projectToken) {
		console.warn('Mixpanel token not found. Mixpanel tracking is disabled.')
		return
	}

	mixpanel.init(projectToken, {
		// Not track page views on route change
		track_pageview: false,
		// Enable debug in development
		debug: import.meta.env.VITE_ENV === 'dev',
		// Disable IP address for privacy
		ip: false,
		// Store user ID to persist across sessions
		persistence: 'localStorage',
	})

	mixpanel.register({
		platform: 'web',
		environment: import.meta.env.VITE_ENV ?? 'unknown',
	})

	return mixpanel
}

// Get the Mixpanel instance
export const getMixpanel = () => {
	return mixpanel
}

// Track custom events
export const trackEvent = (eventName: MixpanelEventName, properties?: Record<string, any>) => {
	try {
		mixpanel.track(eventName, {
			...properties,
			timestamp: new Date().toISOString(),
		})
	} catch (error) {
		console.error('Mixpanel track error:', error)
	}
}

// Identify a user
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
	try {
		mixpanel.identify(userId)
		if (properties) {
			mixpanel.people.set(properties)
		}
	} catch (error) {
		console.error('Mixpanel identify error:', error)
	}
}

// Set user properties
export const setUserProperties = (properties: Record<string, any>) => {
	try {
		mixpanel.people.set(properties)
	} catch (error) {
		console.error('Mixpanel set properties error:', error)
	}
}

// Increment a user property
export const incrementUserProperty = (property: string, value: number = 1) => {
	try {
		mixpanel.people.increment(property, value)
	} catch (error) {
		console.error('Mixpanel increment error:', error)
	}
}

// Track page views
export const trackPageView = (pageName?: string) => {
	try {
		mixpanel.track('Page View', {
			page_name: pageName || window.location.pathname,
			url: window.location.href,
		})
	} catch (error) {
		console.error('Mixpanel page view error:', error)
	}
}

// Reset user data
export const resetMixpanel = () => {
	try {
		mixpanel.reset()
	} catch (error) {
		console.error('Mixpanel reset error:', error)
	}
}

// Opt out of tracking
export const optOutTracking = () => {
	try {
		mixpanel.opt_out_tracking()
	} catch (error) {
		console.error('Mixpanel opt out error:', error)
	}
}

// Opt in to tracking
export const optInTracking = () => {
	try {
		mixpanel.opt_in_tracking()
	} catch (error) {
		console.error('Mixpanel opt in error:', error)
	}
}

export default {
	initMixpanel,
	getMixpanel,
	trackEvent,
	identifyUser,
	setUserProperties,
	incrementUserProperty,
	trackPageView,
	resetMixpanel,
	optOutTracking,
	optInTracking,
}
